import { toast } from 'sonner'
import { create } from 'zustand'

import type { IReportSalesByClientStore } from '@/modules/shared/types/reports/reportSaleByClientStore'
import type { IReportSalesByProductStore } from '@/modules/shared/types/reports/reportSalesByProductStore'
import type { IReportSalesGoalStore } from '@/modules/shared/types/reports/reportSalesGoalStore'
import type { IReportSalesCommission } from '@/modules/shared/types/reports/reposrtSalesCommission'

import { fetchReports } from './report.service'

interface IReportStoreProps {
  loading: boolean
  jsonSalesGoal: IReportSalesGoalStore
  jsonSalesByClient: IReportSalesByClientStore
  jsonSalesByProduct: IReportSalesByProductStore
  jsonSalesCommission: IReportSalesCommission[]
  loadSalesGoal: (
    month: string,
    year: string,
    vendedor?: string,
  ) => Promise<void>
  loadSalesByClient: (
    dateStart: string,
    dateEnd: string,
    vendedor?: string,
  ) => Promise<void>
  loadSalesByProduct: (
    dateStart: string,
    dateEnd: string,
    vendedor?: string,
  ) => Promise<void>
  loadSalesCommission: (
    dateStart: string,
    dateEnd: string,
    vendedor?: string,
  ) => Promise<void>
}

export const useReportStore = create<IReportStoreProps>((set) => {
  let requestId = 0

  async function loadReport(
    field:
      | 'jsonSalesGoal'
      | 'jsonSalesByClient'
      | 'jsonSalesByProduct'
      | 'jsonSalesCommission',
    request: () => ReturnType<ReturnType<typeof fetchReports>['loadSalesGoal']>,
  ) {
    const currentRequest = ++requestId
    set({ loading: true, [field]: null })
    try {
      const response = await request()
      if (currentRequest !== requestId) return
      if (response.data?.status !== 200 && response.data?.status !== 202) {
        throw new Error(
          response.data?.message || 'Não foi possível carregar o relatório.',
        )
      }
      const parsed = JSON.parse(response.data.body)
      const empty = Array.isArray(parsed) && parsed.length === 0
      set({ [field]: empty && field !== 'jsonSalesCommission' ? null : parsed })
    } catch (error) {
      if (currentRequest === requestId) {
        toast.error('Não foi possível carregar o relatório. Tente novamente.')
      }
    } finally {
      if (currentRequest === requestId) set({ loading: false })
    }
  }

  return {
    loading: false,
    jsonSalesGoal: null,
    jsonSalesByClient: null,
    jsonSalesByProduct: null,
    jsonSalesCommission: null,
    loadSalesGoal: (month, year, vendedor) =>
      loadReport('jsonSalesGoal', () =>
        fetchReports().loadSalesGoal(month, year, vendedor),
      ),
    loadSalesByClient: (start, end, vendedor) =>
      loadReport('jsonSalesByClient', () =>
        fetchReports().loadSalesByClient(start, end, vendedor),
      ),
    loadSalesByProduct: (start, end, vendedor) =>
      loadReport('jsonSalesByProduct', () =>
        fetchReports().loadSalesByProduct(start, end, vendedor),
      ),
    loadSalesCommission: (start, end, vendedor) =>
      loadReport('jsonSalesCommission', () =>
        fetchReports().loadSalesCommission(start, end, vendedor),
      ),
  }
})
